import { ApiFetchError, apiClient } from '@borradh-workspace/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { setAdminTabToken } from '@/lib/admin-tab-token';
import { useSession } from '@/lib/session';

import { TwoFactorSetup } from './two-factor-setup';
import { TwoFactorVerify } from './two-factor-verify';

interface TwoFactorGateProps {
  children: React.ReactNode;
}

const PROBE_INTERVAL_MS = 5 * 60 * 1000;

function requiresVerification(error: unknown): boolean {
  return (
    error instanceof ApiFetchError &&
    error.status === 401 &&
    error.code === 'ADMIN_2FA_REQUIRED'
  );
}

export function TwoFactorGate({ children }: TwoFactorGateProps) {
  const { data } = useSession();
  const user = data?.user ?? null;

  return (
    <AdminVerification
      key={user?.id}
      userId={user?.id}
      twoFactorEnabled={user?.twoFactorEnabled ?? false}
    >
      {children}
    </AdminVerification>
  );
}

function AdminVerification({
  children,
  userId,
  twoFactorEnabled,
}: TwoFactorGateProps & { userId?: string; twoFactorEnabled: boolean }) {
  const queryClient = useQueryClient();
  const [setupComplete, setSetupComplete] = useState(false);
  const queryKey = ['admin-terminal', 'verification', userId] as const;
  const verification = useQuery({
    queryKey,
    enabled: Boolean(userId && (twoFactorEnabled || setupComplete)),
    queryFn: async ({ signal }) => {
      // The server checks and renews the signed proof. Also restores this
      // tab's bearer when it inherited verification from another tab.
      const result = await apiClient.get<{ token?: string }>(
        'admin-terminal/session',
        {
          headers: { 'X-Client-Type': 'mobile' },
          signal,
          timeout: 8000,
          retry: 0,
        }
      );
      signal.throwIfAborted();
      if (result?.token) setAdminTabToken(result.token);
      return result;
    },
    staleTime: PROBE_INTERVAL_MS,
    gcTime: 0,
    retryOnMount: true,
    retry: (_attempt, error) =>
      !(error instanceof ApiFetchError) ||
      error.status >= 500 ||
      error.status === 408 ||
      error.status === 429,
    retryDelay: (attempt, error) =>
      error instanceof ApiFetchError && error.status === 429
        ? 60_000
        : Math.min(1000 * 2 ** Math.min(attempt, 3), 8000),
    // A visible terminal with no new requests otherwise loses its 15-minute
    // proof. Renew through the same guard; hidden tabs keep the server TTL.
    refetchInterval: (query) => (query.state.error ? false : PROBE_INTERVAL_MS),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  if (userId && !twoFactorEnabled && !setupComplete) {
    return <TwoFactorSetup onSetupComplete={() => setSetupComplete(true)} />;
  }

  if (requiresVerification(verification.error)) {
    return (
      <TwoFactorVerify
        onVerified={() => queryClient.setQueryData(queryKey, {})}
      />
    );
  }

  // Access denial and a lost login are route errors, not a TOTP challenge.
  if (verification.error) throw verification.error;

  if (!userId || verification.isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    );
  }

  return <>{children}</>;
}
